import React, { useState } from "react"
import dayjs from "dayjs"
import Link from "next/link"
import type { DunningCaseFacts } from "../types"
import { formatDate, formatDateTime, Panel } from "./shared"

/**
 * 詳細画面: ユーザー固有情報（契約タブに依存しない固定表示）。
 * ユーザー facts + 振込先バーチャル口座 + 簡易審査（最新の勤務先・給料日 + 履歴）。
 */

// NULL だけでなく空文字も「—」に倒す（電話番号系は空文字レコードが多い）
const orDash = (value: string | null | undefined) => (value == null || value === "" ? "—" : value)

const paydayLabel = (payday: number | string | null | undefined) => {
  if (payday == null || payday === "") return "—"
  return typeof payday === "number" ? `${payday}日` : String(payday)
}

const birthdayLabel = (birthday: string | null | undefined) => {
  if (!birthday) return "—"

  return `${formatDate(birthday)}（${dayjs().diff(dayjs(birthday), "year")}歳）`
}

export const UserInfoPanel: React.FC<{
  facts: DunningCaseFacts
  userId: number
  /** false のときユーザー詳細へのリンクを出さない（AIドラフト検証の架空ケース表示など、実在しないユーザー） */
  showUserLink?: boolean
}> = ({ facts, userId, showUserLink = true }) => {
  const user = facts.user
  const virtualAccount = facts.virtualAccount
  const examination = facts.simplifiedExaminations?.latest ?? null
  const examinationHistory = facts.simplifiedExaminations?.history ?? []
  const [copiedVirtualAccount, setCopiedVirtualAccount] = useState(false)

  const copyVirtualAccount = async () => {
    if (!virtualAccount) return

    await navigator.clipboard.writeText(
      [
        `銀行名：${virtualAccount.bankName ?? ""}`,
        `支店名：${virtualAccount.branchName ?? ""}`,
        `口座番号：${virtualAccount.accountNumber ?? ""}`,
        `口座名義人：${virtualAccount.accountHolderName ?? ""}`,
      ].join("\n")
    )
    setCopiedVirtualAccount(true)
    window.setTimeout(() => setCopiedVirtualAccount(false), 1500)
  }

  const rows: [string, React.ReactNode][] = [
    ["氏名", user?.fullName ?? "—"],
    ["フリガナ", user?.fullFuriganaName ?? "—"],
    ["生年月日", birthdayLabel(user?.birthday)],
    ["LINEユーザーネーム", orDash(user?.lineUserName)],
    ["電話番号1", orDash(user?.telephoneNumber)],
    ["電話番号2", orDash(user?.telephoneNumber2)],
    [
      "勤務先",
      examination?.companyName ? (
        <span>
          {examination.companyName}
          {examination.companyNameKana && (
            <span className="ml-1 text-[1.1rem] text-black">（{examination.companyNameKana}）</span>
          )}
        </span>
      ) : (
        "—"
      ),
    ],
    ["勤務先電話番号", orDash(examination?.companyTelephoneNumber)],
    ["給料日", paydayLabel(examination?.payday)],
    [
      "バーチャル口座",
      virtualAccount ? (
        <div className="flex items-start gap-3">
          <dl className="grid grid-cols-[80px_1fr] gap-x-2 gap-y-1 tabular-nums">
            <dt className="font-normal text-black">銀行名</dt>
            <dd>{virtualAccount.bankName ?? "—"}</dd>
            <dt className="font-normal text-black">支店名</dt>
            <dd>
              {virtualAccount.branchName ?? "—"}
              {virtualAccount.branchCode && `（${virtualAccount.branchCode}）`}
            </dd>
            <dt className="font-normal text-black">口座番号</dt>
            <dd>{virtualAccount.accountNumber ?? "—"}</dd>
            <dt className="font-normal text-black">口座名義人</dt>
            <dd>{virtualAccount.accountHolderName ?? "—"}</dd>
          </dl>
          <button
            type="button"
            onClick={copyVirtualAccount}
            className="shrink-0 rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-[1.1rem] font-medium text-black hover:border-primary hover:text-primary"
          >
            {copiedVirtualAccount ? "✓" : "📋 コピー"}
          </button>
        </div>
      ) : (
        "未割当"
      ),
    ],
  ]

  return (
    <Panel
      title="ユーザー情報"
      headerRight={
        showUserLink ? (
          <Link
            href={`/admin/users/${userId}`}
            target="_blank"
            rel="noreferrer"
            className="text-[1.2rem] font-medium text-primary hover:opacity-70"
          >
            ユーザー詳細 →
          </Link>
        ) : undefined
      }
    >
      <div className="@container">
        <dl className="grid min-w-0 grid-cols-[minmax(0,130px)_minmax(0,1fr)] gap-x-5 gap-y-3 text-[1.5rem] @[640px]:grid-cols-[130px_minmax(0,1fr)_130px_minmax(0,1fr)]">
          {rows.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt className="text-black">{label}</dt>
              <dd className="min-w-0 break-words font-medium text-black [overflow-wrap:anywhere]">
                {value}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      </div>

      {/* 簡易審査の履歴（最新は上の 勤務先/給料日 に表示済み） */}
      {examinationHistory.length > 0 && (
        <details className="mt-4">
          <summary className="cursor-pointer text-[1.2rem] font-medium text-black hover:text-black">
            簡易審査の履歴（{examinationHistory.length}件）
          </summary>
          <div className="mt-2 overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  {["登録日時", "会社名", "カナ", "勤務先電話番号", "給料日"].map((label) => (
                    <th
                      key={label}
                      className="whitespace-nowrap px-3 py-1.5 text-left text-[1.1rem] font-semibold text-black"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {examinationHistory.map((entry) => (
                  <tr key={entry.id}>
                    <td className="px-3 py-1.5 text-[1.2rem] tabular-nums text-black">
                      {formatDateTime(entry.createdAt)}
                    </td>
                    <td className="px-3 py-1.5 text-[1.25rem] text-black">
                      {entry.companyName ?? "—"}
                    </td>
                    <td className="px-3 py-1.5 text-[1.2rem] text-black">
                      {entry.companyNameKana ?? "—"}
                    </td>
                    <td className="px-3 py-1.5 text-[1.25rem] tabular-nums text-black">
                      {orDash(entry.companyTelephoneNumber)}
                    </td>
                    <td className="px-3 py-1.5 text-[1.25rem] tabular-nums text-black">
                      {paydayLabel(entry.payday)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </Panel>
  )
}
